import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription, timer } from 'rxjs';
import { CountriesListModel } from 'src/app/models/countries-list.model';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { MobileActionWallType, VerificationContractService } from 'src/app/services/Auth/verification-contract.service';
import { CommonService } from 'src/app/services/Common-function/common.service';
import { FormsService } from 'src/app/services/Forms/forms.service';
import { UserService } from 'src/app/services/user-service/user.service';

interface WhatsappVerificationPopupData {
  message?: string;
  wallType?: MobileActionWallType;
  heading?: string;
  label?: string;
  subHeading?: string;
  verificationMethod?: 'whatsapp' | 'surepass';
}

@Component({
  selector: 'ipm-whatsapp-verification',
  templateUrl: './whatsapp-verification.component.html',
  styleUrls: ['./whatsapp-verification.component.scss']
})
export class WhatsappVerificationComponent implements OnInit, OnDestroy {
  @Input() data: WhatsappVerificationPopupData;
  countryListArray: Array<CountriesListModel> = [];
  selectedCountry: CountriesListModel | null = null;
  mobileNumber: string = '';
  otpCode: string = '';
  isOtpStep: boolean = false;
  isSendingOtp: boolean = false;
  isVerifyingOtp: boolean = false;
  isOtpLocked: boolean = false;
  countdown: number = 0;
  errorMessage: string = '';
  successMessage: string = '';
  wallMessage: string = '';
  wallType: MobileActionWallType = 'download';
  heading: string = 'Verify WhatsApp Number';
  label: string = 'WhatsApp Number';
  subHeading: string = 'Enter the OTP sent to your WhatsApp number';
  verificationMethod: 'whatsapp' | 'surepass' = 'whatsapp';
  clientId: string = '';
  private countdownSubscription: Subscription | null = null;

  constructor(
    public commonService: CommonService,
    public formService: FormsService,
    private authService: AuthService,
    private verificationContract: VerificationContractService,
    private userService: UserService,
    @Optional() private dialogRef: MatDialogRef<WhatsappVerificationComponent>,
    @Optional() private bottomSheetRef: MatBottomSheetRef<WhatsappVerificationComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: WhatsappVerificationPopupData,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) private bottomSheetData: WhatsappVerificationPopupData
  ) {}

  ngOnInit(): void {
    this.countryListArray = this.commonService.getCountriesList();
    this.selectedCountry = this.countryListArray.find(
      (country: CountriesListModel) => country.dialCode === '+91'
    ) || null;

    const popupData =
      this.data || this.dialogData || this.bottomSheetData || {};
    this.wallType = popupData?.wallType || 'download';
    this.wallMessage = popupData?.message || this.getDefaultWallMessage();
    this.heading = popupData?.heading || 'Verify WhatsApp Number';
    this.label = popupData?.label || 'WhatsApp Number';
    this.subHeading = popupData?.subHeading || 'Enter the OTP sent to your WhatsApp number';
    this.verificationMethod = popupData?.verificationMethod || 'whatsapp';
  }

  ngOnDestroy(): void {
    this.stopCountdownTimer();
  }

  closeByDeviceType(sendData: any = ''): void {
    if (this.dialogRef) {
      this.dialogRef.close(sendData);
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(sendData);
    } else {
      this.commonService.closingPopupWithSendData(sendData);
    }
  }

  sendOtp(isResend: boolean = false): void {
    const parsedMobileNumber = (this.mobileNumber || '').replace(/\D/g, '');
    const selectedCountryCode = this.selectedCountry?.dialCode || '';

    if (!selectedCountryCode) {
      this.errorMessage = 'Please select country code.';
      this.successMessage = '';
      return;
    }

    if (!parsedMobileNumber) {
      this.errorMessage = 'Please enter your mobile no.';
      this.successMessage = '';
      return;
    }

    this.isSendingOtp = true;
    this.errorMessage = '';

    if (this.verificationMethod === 'surepass') {
      this.userService.generateMobileNumberOtp(parsedMobileNumber).subscribe(
        (response: any) => {
          this.clientId = response?.data?.clientId;
          const normalizedNumber = response?.data?.mobileNumber || parsedMobileNumber;
          
          this.mobileNumber = normalizedNumber;

          this.isOtpStep = true;
          this.isOtpLocked = false;
          this.otpCode = '';
          this.startCountdownTimer();
          this.successMessage = isResend
            ? 'A new OTP has been sent.'
            : 'OTP has been sent.';
          this.isSendingOtp = false;
        },
        (error: any) => {
          const msg = error?.error?.details || error?.error?.error || error?.error?.message || 'Failed to send OTP. Please try again.';
          this.errorMessage = msg;
          this.successMessage = '';
          this.isSendingOtp = false;
        }
      );
    } else {
      this.authService
        .sendMobileVerification({
          isd: selectedCountryCode,
          number: parsedMobileNumber
        })
        .subscribe(
          (response: any) => {
            const normalizedNumber =
              response?.data?.mobile?.number ||
              response?.data?.mobileNumber ||
              parsedMobileNumber;
            const normalizedIsd =
              response?.data?.mobile?.isd || selectedCountryCode;

          this.mobileNumber = normalizedNumber;
          this.selectedCountry = this.countryListArray.find(
            (country: CountriesListModel) => country.dialCode === normalizedIsd
          ) || this.selectedCountry;

          this.isOtpStep = true;
          this.isOtpLocked = false;
          this.otpCode = '';
          this.startCountdownTimer();
          this.successMessage = isResend
            ? 'A new OTP has been sent to your WhatsApp.'
            : 'OTP has been sent to your WhatsApp.';
          this.isSendingOtp = false;
        },
        (error: any) => {
          const msg = this.verificationContract.extractErrorMessage(error);
          const waitMatch = msg.match(/wait (\d+) seconds/i);
          
          if (waitMatch && waitMatch[1]) {
             this.isOtpStep = true;
             this.isOtpLocked = false;
             this.errorMessage = '';
             this.successMessage = '';
             this.startCountdownTimer(parseInt(waitMatch[1], 10));
          } else {
             this.errorMessage = msg;
             this.successMessage = '';
          }
          this.isSendingOtp = false;
        }
      );
    }
  }

  resendOtp(): void {
    if (this.countdown !== 0) {
      return;
    }
    this.sendOtp(true);
  }

  verifyOtp(): void {
    const parsedCode = (this.otpCode || '').replace(/\D/g, '');
    if (!parsedCode) {
      this.errorMessage = 'Please enter the OTP.';
      this.successMessage = '';
      return;
    }

    if (this.isOtpLocked) {
      this.errorMessage = 'Please request a new OTP to continue.';
      this.successMessage = '';
      return;
    }

    this.errorMessage = '';
    this.isVerifyingOtp = true;

    if (this.verificationMethod === 'surepass') {
      this.userService.verifyMobileOTP(this.clientId, parsedCode).subscribe(
        (response: any) => {
          this.authService.getCurrentUserDetails().subscribe(
            (userDetails) => {
              if (userDetails) {
                this.authService.currentUserDetails.next(userDetails);
              }
              this.isVerifyingOtp = false;
              this.commonService.showToastSuccess('Mobile number verified successfully.');
              this.closeByDeviceType('verified');
            },
            () => {
              this.isVerifyingOtp = false;
              this.commonService.showToastSuccess('Mobile number verified successfully.');
              this.closeByDeviceType('verified');
            }
          );
        },
        (error: any) => {
          this.errorMessage = error?.error?.details || error?.error?.error || 'Something went wrong, please try again.';
          this.successMessage = '';
          this.isVerifyingOtp = false;
        }
      );
    } else {
      this.authService.verifyMobileVerification(parsedCode).subscribe(
        (response: any) => {
          const userObject = response?.data?.user;
          if (userObject && Object.keys(userObject).length > 0) {
            this.authService.currentUserDetails.next(userObject);
          }

        this.authService.getCurrentUserDetails().subscribe(
          (userDetails) => {
            if (userDetails) {
              this.authService.currentUserDetails.next(userDetails);
            }
            this.isVerifyingOtp = false;
            this.commonService.showToastSuccess('WhatsApp number verified successfully.');
            this.closeByDeviceType('verified');
          },
          () => {
            this.isVerifyingOtp = false;
            this.commonService.showToastSuccess('WhatsApp number verified successfully.');
            this.closeByDeviceType('verified');
          }
        );
      },
      (error: any) => {
        this.errorMessage = this.verificationContract.extractErrorMessage(error);
        this.successMessage = '';
        if (this.verificationContract.isOtpLockout(error)) {
          this.isOtpLocked = true;
          this.otpCode = '';
          this.startCountdownTimer();
        }
        this.isVerifyingOtp = false;
      }
    );
   }
  }

  onMobileNumberChange(value: string): void {
    this.mobileNumber = (value || '').replace(/\D/g, '').slice(0, 15);
  }

  updateOtpCode(value: string): void {
    this.otpCode = (value || '').replace(/\D/g, '');
  }

  editNumber(): void {
    this.isOtpStep = false;
    this.otpCode = '';
    this.errorMessage = '';
    this.successMessage = '';
    this.isOtpLocked = false;
  }

  private startCountdownTimer(seconds: number = 60): void {
    this.stopCountdownTimer();
    this.countdown = seconds;
    this.countdownSubscription = timer(0, 1000).subscribe(() => {
      this.countdown = this.countdown - 1;
      if (this.countdown <= 0) {
        this.countdown = 0;
        this.stopCountdownTimer();
      }
    });
  }

  private stopCountdownTimer(): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = null;
    }
  }

  private getDefaultWallMessage(): string {
    if (this.wallType === 'play') {
      return 'You have reached your play limit. Verify your WhatsApp number to continue playing.';
    }
    if (this.wallType === 'join-eventlist') {
      return 'Verify your WhatsApp number to join the eventlist and get session reminders.';
    }
    return 'Verify your WhatsApp number to continue downloading.';
  }
}
